import React, { FC } from "react";
import { useTranslation } from "Providers/TranslationProvider";
import { useForm } from "react-hook-form";
import ErrorView from "Views/ErrorView";
import { useApp } from "Providers/AppProvider";
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	makeStyles,
} from "@material-ui/core";
import { color, px } from "Configs/theme";
import * as yup from "yup";
import TextField from "Fields/TextField";
import { useRedirect } from "Helpers/RedirectHelper";
import { useSnackbar } from "material-ui-snackbar-provider";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useAuthUser } from "./hooks/useAuthUser";

interface FormValues {
	email: string;
	password: string;
}

const AuthSignInView: FC = () => {
	const classes = useStyles();
	const { isStandaloneApp, isNative } = useApp();
	const { t } = useTranslation();
	const redirect = useRedirect();
	const snackbar = useSnackbar();

	const defaultValues: FormValues = {
		email: "",
		password: "",
	};

	const validationSchema = yup.object().shape({
		email: yup
			.string()
			.email()
			.required(
				t("Common_Form_Error_MandatoryField", {
					field: t("SignIn_Email"),
				})
			),
		password: yup.string().required(
			t("Common_Form_Error_MandatoryField", {
				field: t("SignIn_Password"),
			})
		),
	});

	async function onSubmit(formValues: FormValues) {
		try {
			await useAuthUser
				.getState()
				.signIn(formValues.email, formValues.password);
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const isDisabled = form.formState.isSubmitting;

	if (!isStandaloneApp) {
		return <ErrorView error={new Error(t("SignIn_Error_LoggedOut"))} />;
	}

	return (
		<form className={classes.form} onSubmit={form.handleSubmit(onSubmit)}>
			<Card className={classes.card}>
				<CardContent>
					<Box className={classes.title}>{t("SignIn_Title")}</Box>
					<Box mt={2}>{t("SignIn_Label")}</Box>
					<Box mt={3}>
						<TextField
							name="email"
							type="email"
							label={t("SignIn_Email")}
							control={control}
							disabled={isDisabled}
							autoFocus={!isNative}
						/>
					</Box>
					<Box mt={3}>
						<TextField
							name="password"
							type="password"
							label={t("SignIn_Password")}
							control={control}
							disabled={isDisabled}
						/>
					</Box>
				</CardContent>
				<CardActions>
					<Button onClick={() => redirect("password-forgotten")}>
						{t("SignIn_ForgotPassword")}
					</Button>
					<Box display="flex" flex={1} />
					<Button type="submit" color="primary" disabled={isDisabled}>
						{t("SignIn_Login")}
					</Button>
				</CardActions>
			</Card>
		</form>
	);
};

export default AuthSignInView;

const useStyles = makeStyles({
	form: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flex: 1,
		backgroundColor: color.pageBackground,
		padding: px.xl,
	},
	card: {
		width: "100%",
		maxWidth: 460,
	},
	title: {
		textTransform: "uppercase",
		fontWeight: 800,
		letterSpacing: 0,
		fontSize: "16px",
	},
});
